/**
 * Module NEUTRE « button » — KEEP IN SYNC avec
 * inventozen-frontpublic/src/lib/cms-puck/blocks/button.ts (comme block-style/ et layout/).
 *
 * Types + fonctions PURES du bloc Bouton (lien interne/externe, bordures par côté,
 * radius par coin, icône, hover). AUCUN import React/Puck/UI. Réutilise `ColorRef`
 * + `resolveColorRef` du module block-style. Stocké dans le JSON de page (opaque
 * côté API → aucune migration). Rétro-compat : un bouton legacy (`url` string +
 * `openInNew`) est lu via `resolveButtonLink`.
 */

import {
  normalizeColorRef,
  resolveColorRef,
  type ColorRef,
} from "@/lib/cms-puck/block-style";
import { type CmsColor } from "@/lib/cms/cms-theme.constants";
import { cmsNew } from "@/lib/cms/cms-new-i18n";

/* ----------------------------- Lien ------------------------------------- */

export type ButtonLinkTarget =
  | "PAGE"
  | "CUSTOM"
  | "SYSTEM"
  | "CATALOG_DECOUPAGE"
  | "NONE";

export type CatalogDecoupageLink = {
  target: "ARTWORKS" | "MULTIPRINTS";
  decoupageId: string | null;
};

export type ButtonLink = {
  targetType: ButtonLinkTarget;
  /** Page interne (CMS avancé) si `targetType==="PAGE"`. */
  pageId: string | null;
  /** URL libre (externe ou chemin) si `targetType==="CUSTOM"`. */
  url: string | null;
  /** Clé de route système (cf. `SYSTEM_ROUTES`) si `targetType==="SYSTEM"`. */
  systemKey: string | null;
  /** Listing d'un découpage si `targetType==="CATALOG_DECOUPAGE"`. */
  catalogDecoupage: CatalogDecoupageLink | null;
  openInNew: boolean;
};

export const DEFAULT_CATALOG_DECOUPAGE_LINK: CatalogDecoupageLink = {
  target: "ARTWORKS",
  decoupageId: null,
};

export function catalogDecoupageLinkKey(
  link: CatalogDecoupageLink,
): string | null {
  if (!link.decoupageId) return null;
  return `${link.target}|${link.decoupageId}`;
}

/**
 * Routes « système » du site NON refondues en CMS avancé, à chemin FIXE (auth
 * d'œuvres, abonnement, comptes, paiement…). L'URL finale est localisée côté API
 * (`localePath(lang, path, defaultLang)`) à la résolution. KEEP IN SYNC avec la
 * map côté API (`cms-puck.service.ts` SYSTEM_ROUTE_PATHS).
 */
const SYSTEM_ROUTE_DEFS: { key: string; labelKey: string; labelFr: string; path: string }[] = [
  { key: "artwork-auth-intro", labelKey: "cms.new.options.systemRoute.artworkAuthIntro", labelFr: "Authentification d'œuvres — introduction", path: "expertise/introduction" },
  { key: "artwork-auth-form", labelKey: "cms.new.options.systemRoute.artworkAuthForm", labelFr: "Authentification d'œuvres — formulaire", path: "expertise/formulaire" },
  { key: "abonnement", labelKey: "cms.new.options.systemRoute.abonnement", labelFr: "Abonnement", path: "abonnement" },
  { key: "connexion", labelKey: "cms.new.options.systemRoute.connexion", labelFr: "Connexion", path: "connexion" },
  { key: "inscription", labelKey: "cms.new.options.systemRoute.inscription", labelFr: "Inscription", path: "inscription" },
  { key: "profil", labelKey: "cms.new.options.systemRoute.profil", labelFr: "Profil", path: "profil" },
  { key: "favorites", labelKey: "cms.new.options.systemRoute.favorites", labelFr: "Favoris", path: "favorites" },
  { key: "payment", labelKey: "cms.new.options.systemRoute.payment", labelFr: "Paiement", path: "payment" },
  // Chemins dynamiques (présentation découpages CR) — résolus côté API.
  { key: "catalog-artworks", labelKey: "cms.new.options.systemRoute.catalogArtworks", labelFr: "Catalogue — Œuvres (découpages)", path: "catalog-artworks" },
  { key: "catalog-multiprints", labelKey: "cms.new.options.systemRoute.catalogMultiprints", labelFr: "Catalogue — Multiples (découpages)", path: "catalog-multiprints" },
];

export function getSystemRoutes(): { key: string; label: string; path: string }[] {
  return SYSTEM_ROUTE_DEFS.map((r) => ({
    key: r.key,
    label: cmsNew(r.labelKey as `cms.new.${string}`, r.labelFr),
    path: r.path,
  }));
}

/** Lien système vers la présentation des découpages CR (œuvres / multiples). */
export function catalogSystemLinkForTarget(
  target: "ARTWORKS" | "MULTIPRINTS" = "ARTWORKS",
): ButtonLink {
  return {
    targetType: "SYSTEM",
    pageId: null,
    url: null,
    systemKey:
      target === "MULTIPRINTS" ? "catalog-multiprints" : "catalog-artworks",
    catalogDecoupage: null,
    openInNew: false,
  };
}


export const DEFAULT_BUTTON_LINK: ButtonLink = {
  targetType: "CUSTOM",
  pageId: null,
  url: "#",
  systemKey: null,
  catalogDecoupage: null,
  openInNew: false,
};

/**
 * Lien OPTIONNEL par défaut (= aucun lien) pour les blocs où le lien n'est pas
 * obligatoire (titre, image). `url` vide → lien non « explicite » → rendu NU.
 * KEEP IN SYNC avec frontpublic.
 */
export const DEFAULT_OPTIONAL_LINK: ButtonLink = {
  targetType: "NONE",
  pageId: null,
  url: null,
  systemKey: null,
  catalogDecoupage: null,
  openInNew: false,
};

function normalizeCatalogDecoupageLink(value: unknown): CatalogDecoupageLink | null {
  if (!value || typeof value !== "object") return null;
  const l = value as Partial<CatalogDecoupageLink>;
  const target = l.target === "MULTIPRINTS" ? "MULTIPRINTS" : "ARTWORKS";
  return {
    target,
    decoupageId:
      typeof l.decoupageId === "string" && l.decoupageId ? l.decoupageId : null,
  };
}

export function normalizeButtonLink(value: unknown): ButtonLink {
  const l = (value ?? {}) as Partial<ButtonLink>;
  const targetType: ButtonLinkTarget =
    l.targetType === "PAGE"
      ? "PAGE"
      : l.targetType === "SYSTEM"
        ? "SYSTEM"
        : l.targetType === "CATALOG_DECOUPAGE"
          ? "CATALOG_DECOUPAGE"
          : l.targetType === "NONE"
            ? "NONE"
            : "CUSTOM";
  return {
    targetType,
    pageId: typeof l.pageId === "string" && l.pageId ? l.pageId : null,
    url: typeof l.url === "string" ? l.url : targetType === "CUSTOM" ? "#" : null,
    systemKey:
      typeof l.systemKey === "string" && l.systemKey ? l.systemKey : null,
    catalogDecoupage: normalizeCatalogDecoupageLink(l.catalogDecoupage),
    openInNew: l.openInNew === true,
  };
}

/**
 * Résout le lien d'un bouton depuis ses props, en tolérant les boutons LEGACY
 * (qui n'ont que `url:string` + `openInNew`) → lien CUSTOM synthétisé.
 */
export function resolveButtonLink(raw: {
  link?: unknown;
  url?: unknown;
  openInNew?: unknown;
}): ButtonLink {
  const legacyUrl =
    typeof raw.url === "string" && raw.url.trim() ? raw.url : null;
  const link =
    raw.link && typeof raw.link === "object" ? normalizeButtonLink(raw.link) : null;
  // Lien EXPLICITEMENT défini (page interne choisie, page système choisie, ou URL
  // custom non vide/≠"#") → prioritaire. Sinon on retombe sur l'`url` LEGACY
  // (bouton d'avant la refonte ; Puck a pu injecter un `link` par défaut qui ne
  // doit pas masquer l'ancien url).
  const explicit =
    link != null &&
    (link.targetType === "NONE"
      ? false
      : link.targetType === "PAGE"
      ? !!link.pageId
      : link.targetType === "SYSTEM"
        ? !!link.systemKey
        : link.targetType === "CATALOG_DECOUPAGE"
          ? !!catalogDecoupageLinkKey(
              link.catalogDecoupage ?? DEFAULT_CATALOG_DECOUPAGE_LINK,
            )
          : !!link.url && link.url !== "#");
  if (explicit) return link as ButtonLink;
  if (legacyUrl) {
    return {
      targetType: "CUSTOM",
      pageId: null,
      url: legacyUrl,
      systemKey: null,
      catalogDecoupage: null,
      openInNew: raw.openInNew === true || (link?.openInNew ?? false),
    };
  }
  return link ?? { ...DEFAULT_BUTTON_LINK };
}

/* ----------------------------- Icône ------------------------------------ */

export type IconPosition = "before" | "after";

/* ---------------------------- Bordures ---------------------------------- */

export type BorderStyle = "solid" | "dashed" | "dotted";

export type BorderSide = {
  /** Largeur en px ; `null` = pas de bordure sur ce côté. */
  width: number | null;
  style: BorderStyle;
  color: ColorRef;
};

export type ButtonBorder = {
  top: BorderSide;
  right: BorderSide;
  bottom: BorderSide;
  left: BorderSide;
};

export type ButtonRadius = {
  tl: number | null;
  tr: number | null;
  br: number | null;
  bl: number | null;
};

export type ButtonHover = {
  background: ColorRef;
  text: ColorRef;
  borderColor: ColorRef;
};

const DEFAULT_BORDER_SIDE: BorderSide = { width: null, style: "solid", color: null };

export const DEFAULT_BUTTON_BORDER: ButtonBorder = {
  top: { ...DEFAULT_BORDER_SIDE },
  right: { ...DEFAULT_BORDER_SIDE },
  bottom: { ...DEFAULT_BORDER_SIDE },
  left: { ...DEFAULT_BORDER_SIDE },
};

export const DEFAULT_BUTTON_RADIUS: ButtonRadius = {
  tl: null,
  tr: null,
  br: null,
  bl: null,
};

export const DEFAULT_BUTTON_HOVER: ButtonHover = {
  background: null,
  text: null,
  borderColor: null,
};

const BORDER_STYLES: readonly BorderStyle[] = ["solid", "dashed", "dotted"];

function normalizeSideNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeBorderSide(value: unknown): BorderSide {
  const s = (value ?? {}) as Partial<BorderSide>;
  return {
    width: normalizeSideNumber(s.width),
    style: BORDER_STYLES.includes(s.style as BorderStyle)
      ? (s.style as BorderStyle)
      : "solid",
    color: normalizeColorRef(s.color),
  };
}

export function normalizeButtonBorder(value: unknown): ButtonBorder {
  const b = (value ?? {}) as Partial<ButtonBorder>;
  return {
    top: normalizeBorderSide(b.top),
    right: normalizeBorderSide(b.right),
    bottom: normalizeBorderSide(b.bottom),
    left: normalizeBorderSide(b.left),
  };
}

export function normalizeButtonRadius(value: unknown): ButtonRadius {
  const r = (value ?? {}) as Partial<ButtonRadius>;
  return {
    tl: normalizeSideNumber(r.tl),
    tr: normalizeSideNumber(r.tr),
    br: normalizeSideNumber(r.br),
    bl: normalizeSideNumber(r.bl),
  };
}

export function normalizeButtonHover(value: unknown): ButtonHover {
  const h = (value ?? {}) as Partial<ButtonHover>;
  return {
    background: normalizeColorRef(h.background),
    text: normalizeColorRef(h.text),
    borderColor: normalizeColorRef(h.borderColor),
  };
}

export type ButtonAppearance = {
  border: ButtonBorder;
  radius: ButtonRadius;
  hover: ButtonHover;
};

export const DEFAULT_BUTTON_APPEARANCE: ButtonAppearance = {
  border: DEFAULT_BUTTON_BORDER,
  radius: DEFAULT_BUTTON_RADIUS,
  hover: DEFAULT_BUTTON_HOVER,
};

export function normalizeButtonAppearance(value: unknown): ButtonAppearance {
  const a = (value ?? {}) as Partial<ButtonAppearance>;
  return {
    border: normalizeButtonBorder(a.border),
    radius: normalizeButtonRadius(a.radius),
    hover: normalizeButtonHover(a.hover),
  };
}

/* --------------------------- Résolution CSS ----------------------------- */

const SIDE_CSS: Array<[keyof ButtonBorder, string]> = [
  ["top", "Top"],
  ["right", "Right"],
  ["bottom", "Bottom"],
  ["left", "Left"],
];

type BorderWidthSides = {
  top: { width: number | null };
  right: { width: number | null };
  bottom: { width: number | null };
  left: { width: number | null };
};

/** Les 4 côtés ont une largeur explicite à 0 (supprime la bordure preset outline). */
export function allSidesBorderWidthZero(border: BorderWidthSides): boolean {
  return (
    border.top.width === 0 &&
    border.right.width === 0 &&
    border.bottom.width === 0 &&
    border.left.width === 0
  );
}

/** Styles de bouton ; `link` = texte seul, sans fond (liens de pied de page…). */
export type ButtonVariant = "primary" | "secondary" | "outline" | "link";

const BUTTON_VARIANT_BASE: Record<ButtonVariant, string> = {
  primary: "bg-slate-900 text-white hover:bg-slate-700",
  secondary: "bg-slate-200 text-slate-900 hover:bg-slate-300",
  outline: "text-slate-900 hover:bg-slate-100 bg-transparent",
  link: "bg-transparent hover:opacity-80",
};

/** Classes de variante — sans bordure preset si `suppressDefaultBorder`. */
export function resolveButtonVariantClass(
  variant: ButtonVariant,
  suppressDefaultBorder = false,
  suppressDefaultBorderColor = false,
): string {
  const base = BUTTON_VARIANT_BASE[variant];
  if (variant === "outline" && !suppressDefaultBorder) {
    const colorCls = suppressDefaultBorderColor ? "" : " border-slate-400";
    return `${base} border${colorCls}`;
  }
  return base;
}

/**
 * CSS inline des bordures (par côté) + radius (par coin). N'émet QUE les valeurs
 * définies. Largeur `0` → bordure masquée (`width: 0`, `style: none`).
 */
export function resolveButtonBoxCss(
  palette: CmsColor[],
  border: ButtonBorder,
  radius: ButtonRadius,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [side, suffix] of SIDE_CSS) {
    const s = border[side];
    const color = resolveColorRef(palette, s.color);
    if (s.width != null) {
      if (s.width === 0) {
        out[`border${suffix}Width`] = "0";
        out[`border${suffix}Style`] = "none";
      } else {
        out[`border${suffix}Width`] = `${s.width}px`;
        out[`border${suffix}Style`] = s.style;
        out[`border${suffix}Color`] = color ?? "currentColor";
      }
    } else if (color) {
      out[`border${suffix}Color`] = color;
    }
  }
  if (radius.tl != null) out.borderTopLeftRadius = `${radius.tl}px`;
  if (radius.tr != null) out.borderTopRightRadius = `${radius.tr}px`;
  if (radius.br != null) out.borderBottomRightRadius = `${radius.br}px`;
  if (radius.bl != null) out.borderBottomLeftRadius = `${radius.bl}px`;
  return out;
}

/** Le bouton a-t-il un radius custom (pour retirer la classe `rounded`) ? */
export function hasCustomRadius(radius: ButtonRadius): boolean {
  return (
    radius.tl != null || radius.tr != null || radius.br != null || radius.bl != null
  );
}

/**
 * Règle CSS `:hover` scopée à `.className` (le style inline ne gère pas `:hover`).
 * Émise uniquement pour les couleurs définies ; chaîne vide si aucun hover.
 * `!important` : le fond/texte/bordure du Design est posé en style inline,
 * qui l'emporterait sinon sur la règle `:hover`.
 */
export function buildHoverCss(
  className: string,
  hover: ButtonHover,
  palette: CmsColor[],
): string {
  const decls: string[] = [];
  const bg = resolveColorRef(palette, hover.background);
  if (bg) decls.push(`background-color:${bg} !important`);
  const tx = resolveColorRef(palette, hover.text);
  if (tx) decls.push(`color:${tx} !important`);
  const bc = resolveColorRef(palette, hover.borderColor);
  if (bc) decls.push(`border-color:${bc} !important`);
  if (!decls.length) return "";
  return `.${className}:hover{${decls.join(";")}}`;
}
